import { isPlatformBrowser } from '@angular/common';
import type { HttpEvent, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, tap, throwError, finalize } from 'rxjs';

import { ApiLoaderService } from './api-loader.service';
import { EncryptionService } from './encryption.service';
import { NetworkService } from './network.service';

@Injectable({ providedIn: 'root' })
export class ApiLoaderInterceptorService {
  encryptionService = inject(EncryptionService);
  loaderService = inject(ApiLoaderService);
  router = inject(Router);
  networkService = inject(NetworkService);
  platformId = inject(PLATFORM_ID);

  intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const IS_BROWSER = isPlatformBrowser(this.platformId);

    const IS_PUBLIC = req.headers.get('isPublic') === 'true';
    const HIDE_LOADER = req.headers.get('hideLoader') === 'true';

    // Get token only on browser
    const TOKEN = IS_BROWSER
      ? this.encryptionService.decodeBase64(localStorage.getItem('token'))
      : null;

    // Show loader
    if (!HIDE_LOADER && IS_BROWSER) {
      this.loaderService.show();
    }

    // Clean custom headers
    let headers = req.headers.delete('isPublic').delete('hideLoader');

    // Add token if not public
    if (!IS_PUBLIC && TOKEN) {
      headers = headers.set('Authorization', `${TOKEN}`);
    }

    const MODIFIED_REQUEST = req.clone({ headers });

    return next.handle(MODIFIED_REQUEST).pipe(
      tap((event: HttpEvent<T>) => {
        if (event instanceof HttpResponse && IS_BROWSER) {
          this.networkService.setOnlineStatus(true);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (IS_BROWSER) {
          if (error.status === 0 && !navigator.onLine) {
            this.networkService.setOnlineStatus(false);
          }

          if (error.status === 401) {
            localStorage.clear();
            this.router.navigateByUrl('/');
          }
        }

        return throwError(() => error);
      }),
      finalize(() => {
        if (!HIDE_LOADER && IS_BROWSER) {
          this.loaderService.hide();
        }
      })
    );
  }
}
